/**
 * Убирает `token` из адресной строки ДО инициализации аналитики.
 *
 * Зачем: gtag.js при загрузке сам читает `location.href` и отправляет его в
 * `page_location` первого page_view. Если пользователь пришёл по ссылке с
 * `?token=<JWT>` (автологин из писем и соцсетей), JWT утекает в аналитику.
 * Отключение `send_page_view` не помогает — скрипт всё равно снимает URL при
 * старте, поэтому единственный надёжный способ — почистить адрес раньше него.
 *
 * Токен не теряется: возвращаем его вызывающему коду, а адресную строку чистим
 * здесь же. Применяет токен `router/middleware.ts` — там же, где и раньше,
 * чтобы не дублировать логику logout прошлого пользователя.
 *
 * Вызывать ДО `createWebHistory()` (сейчас — в `router/config.ts`): history
 * снимает `location.href` при создании и на первой навигации переписывает
 * адресную строку из этого снимка. Очистка после создания history возвращала
 * `?token` на маршрутах без `allowUrlToken`, например на `/home` (MOB-508).
 * Там же `stripVisitorIdParams()` снимает идентичность посетителя `_ac_cid` /
 * `_ac_ref` (MOB-586) — по той же причине: её нельзя отдавать счётчикам.
 */
import Logger from '@web/common/Logger'
import { withoutQueryParamsDeep } from '@web/common/url'

const TOKEN_PARAM = 'token'

/**
 * Токен, снятый с URL до старта роутера. Роутер уже не увидит его в query,
 * поэтому передаём значение через модуль — читает `router/middleware.ts`.
 */
let pendingUrlToken: string | null = null

/** Возвращает снятый с URL токен и забывает его (одноразовое чтение). */
export function consumePendingUrlToken (): string | null {
  const token = pendingUrlToken
  pendingUrlToken = null
  return token
}

type RouteQuery = Record<string, unknown>

/**
 * Возврат из входа через соцсеть: платформа редиректит на страницу входа с
 * `?action=login&status=success&token=<JWT>`. Токен там применяет
 * `useLoginForm` (вместе с user/fetch, событием login и afterLogin), а не
 * middleware — у страниц входа нет `allowUrlToken`.
 */
export function isSocialLoginReturn (query: RouteQuery): boolean {
  return query.action === 'login' && query.status === 'success'
}

/**
 * Токен для middleware: из query (переходы внутри SPA) или снятый с URL при
 * загрузке. На возврате из соцсети снятый токен не забираем — иначе middleware
 * его выбросит, и `useLoginForm` останется без токена.
 */
export function takeUrlTokenForRoute (query: RouteQuery): { queryToken: string | null; urlToken: string | null } {
  const raw = query.token
  const queryToken = (Array.isArray(raw) ? raw[0] : raw) as string | null | undefined
  if (queryToken) {
    return { queryToken: String(queryToken), urlToken: String(queryToken) }
  }
  if (isSocialLoginReturn(query)) {
    return { queryToken: null, urlToken: null }
  }
  return { queryToken: null, urlToken: consumePendingUrlToken() }
}

/**
 * Вырезает `token` из текущего URL.
 *
 * @returns значение токена, если он был в адресе, иначе `null`.
 */
export function stripUrlToken (): string | null {
  // window может отсутствовать в SSR/тестовом окружении.
  if (typeof window === 'undefined' || !window.location) return null

  try {
    const url = new URL(window.location.href)
    const urlToken = url.searchParams.get(TOKEN_PARAM)
    if (!urlToken) return null

    url.searchParams.delete(TOKEN_PARAM)
    // replaceState не перезагружает страницу и не добавляет запись в историю,
    // поэтому кнопка «Назад» не вернёт пользователя на URL с токеном.
    window.history.replaceState(window.history.state, '', url.toString())

    pendingUrlToken = urlToken
    return urlToken
  } catch (error) {
    // Аналитика не должна ломать загрузку приложения из-за кривого URL.
    Logger.error('stripUrlToken failed', error)
    return null
  }
}

const VISITOR_ID_PARAMS = ['_ac_cid', '_ac_ref']

/**
 * Вырезает идентичность посетителя (`_ac_cid`, `_ac_ref`) из адреса входа в SPA.
 * Платформа кладёт её в адреса, чтобы визит не обрывался без кук; SPA она не
 * нужна, а gtag/Метрика иначе унесут её в page_location сторонним сервисам.
 * Вызывать там же и тогда же, что и stripUrlToken, — до createWebHistory().
 */
export function stripVisitorIdParams (): void {
  if (typeof window === 'undefined' || !window.location) return

  try {
    // Не URLSearchParams.delete: тот пересобирает query целиком (%20 → +,
    // ?flag → ?flag=) и переписывал бы адрес почти на каждом входе. Deep — потому
    // что _ac_* приходит и внутри закодированного returnUrl.
    const next = withoutQueryParamsDeep(window.location.href, VISITOR_ID_PARAMS)
    if (next !== window.location.href) {
      window.history.replaceState(window.history.state, '', next)
    }
  } catch (error) {
    Logger.error('stripVisitorIdParams failed', error)
  }
}

export default stripUrlToken
